import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  RiCalendarLine,
  RiSearchLine,
  RiMore2Line,
  RiEyeLine,
} from "@remixicon/react";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import api from "@/utils/api";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { getCurrencySymbol } from "@/utils/currency";
import { statusBadgeClass } from "@/lib/status";

const bookingStatuses = [
  { label: "All Statuses", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Completed", value: "completed" },
  { label: "No Show", value: "no_show" },
];

interface Booking {
  _id: string;
  bookingNumber: string;
  slot: {
    slotId: {
      _id: string;
      name: string;
      bookingType: string;
    };
    snapshot: {
      name: string;
      bookingType: string;
    };
  };
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  bookingDetails: {
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
    quantity: number;
  };
  pricing: {
    total: number;
    currency: string;
  };
  status: string;
  payment: {
    status: string;
    method: string;
  };
  createdAt: string;
}

export function BookingList() {
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
    search: "",
    page: 1,
    limit: 20,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { storeId } = useCurrentStore();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchInput,
        page: 1,
      }));
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all") {
        params.append(key, value.toString());
      }
    });

    return params.toString();
  }, [filters]);

  const { data: bookingsData, isLoading } = useQuery({
    queryKey: ["bookings", queryParams, storeId],
    queryFn: async () => {
      const { data } = await api.get(`/api/bookings?${queryParams}`, {
        headers: { "Store-ID": storeId },
      });
      return data;
    },
    enabled: !!storeId && !!queryParams,
  });

  const bookings = bookingsData?.bookings || [];
  const pagination = bookingsData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 };

  const getStatusColor = statusBadgeClass;
  const getPaymentStatusColor = statusBadgeClass;

  if (isMobile) {
    return (
      <div className="space-y-4">
        <SectionCard>
          <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search bookings..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={`${dashboardStyles.filterInput} pl-9`}
            />
          </div>
          <Select
            value={filters.status}
            onValueChange={(value) => setFilters({ ...filters, status: value })}
          >
            <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-[180px]`}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {bookingStatuses.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className={dashboardStyles.listItem}>
                <div className="w-full">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <Skeleton className="h-4 w-28 mb-1.5" />
                      <Skeleton className="h-3 w-36" />
                      <Skeleton className="h-3 w-28 mt-1" />
                    </div>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : bookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 px-4 rounded-xl bg-muted/20">
            <div className={dashboardStyles.emptyIcon}>
              <RiCalendarLine className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className={dashboardStyles.emptyTitle}>No bookings found</p>
            <p className={dashboardStyles.emptyDescription}>
              New bookings will appear here once customers complete a booking.
            </p>
          </div>
        ) : (
          <div className={dashboardStyles.listContainer}>
            {bookings.map((booking: Booking) => (
              <div
                key={booking._id}
                className={dashboardStyles.listItem}
                onClick={() => navigate(`/dashboard/bookings/${booking._id}`)}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={dashboardStyles.listItemIcon}>
                    <RiCalendarLine className={dashboardStyles.listItemIconSvg} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm leading-tight truncate">
                      {booking.bookingNumber}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {booking.slot.snapshot.name} · {booking.customer.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {format(new Date(booking.bookingDetails.startDate), "MMM d, yyyy")}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <div className="font-semibold text-sm font-heading leading-tight">
                    {getCurrencySymbol(booking.pricing.currency || 'NGN')}
                    {booking.pricing.total}
                  </div>
                  <div className="flex gap-1 justify-end mt-0.5 flex-wrap">
                    <Badge className={getStatusColor(booking.status)}>
                      {booking.status}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionCard>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by customer, booking #, or slot..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${dashboardStyles.filterInput} pl-9`}
              />
            </div>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-[200px]`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {bookingStatuses.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className={dashboardStyles.tableWrap}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking #</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 7 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-28 mb-1" />
                        <Skeleton className="h-3 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24 mb-1" />
                        <Skeleton className="h-3 w-16" />
                      </TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-7 w-7 rounded-md ml-auto" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : bookings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 rounded-xl bg-muted/20">
              <div className={dashboardStyles.emptyIcon}>
                <RiCalendarLine className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className={dashboardStyles.emptyTitle}>No bookings found</p>
              <p className={dashboardStyles.emptyDescription}>
                New bookings will appear here once customers complete a booking.
              </p>
            </div>
          ) : (
            <>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Booking #</TableHead>
                  <TableHead>Slot</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((booking: Booking) => (
                  <TableRow key={booking._id}>
                    <TableCell className="font-medium">
                      {booking.bookingNumber}
                    </TableCell>
                    <TableCell>
                      {booking.slot.snapshot.name}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{booking.customer.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {booking.customer.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {format(new Date(booking.bookingDetails.startDate), "MMM d, yyyy")}
                      {booking.bookingDetails.startTime && (
                        <div className="text-sm text-muted-foreground">
                          {booking.bookingDetails.startTime}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {getCurrencySymbol(booking.pricing.currency || 'NGN')}
                      {booking.pricing.total}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(booking.status)}>
                        {booking.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPaymentStatusColor(booking.payment.status)}>
                        {booking.payment.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <RiMore2Line className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => navigate(`/dashboard/bookings/${booking._id}`)}
                          >
                            <RiEyeLine className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
                {pagination.total} bookings
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
                  disabled={filters.page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ ...filters, page: Math.min(pagination.pages, filters.page + 1) })}
                  disabled={filters.page === pagination.pages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

